import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { linkedArtefactsApi } from '../services/api';
import { NimbusTable } from './NimbusTable';

interface LinkedArtefact {
  linkId: string;
  artefact_id: string;
  artefact_display_id?: string;
  artefact_type: string;
  artefact_register: string;
  artefact_title: string;
  artefact_accountable: string;
}

interface SearchResult {
  id: string;
  display_id?: string;
  type: string;
  register: string;
  title: string;
  accountable?: string;
}

interface Props {
  artefactId: string;
  artefactType: string;
  artefactRegister: string;
  matrixId?: string;
  registerId?: string;
  readOnly?: boolean;
}

export const LinkedArtefacts: React.FC<Props> = ({ artefactId, artefactType, artefactRegister, matrixId, registerId, readOnly }) => {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const { data: linksData, isLoading } = useQuery({
    queryKey: ['linkedArtefacts', artefactId, artefactType],
    queryFn: () => linkedArtefactsApi.getLinks(artefactId, artefactType),
    enabled: !!artefactId,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  const links: LinkedArtefact[] = linksData?.links || [];
  const filteredLinks = links.filter((link) => {
    if (typeFilter && link.artefact_type !== typeFilter) return false;
    return true;
  });

  const createLinksMutation = useMutation({
    mutationFn: (data: any) => linkedArtefactsApi.createLinks(data),
    // Links are bidirectional on the backend - the other side's own linkedArtefacts cache entry
    // (e.g. the initiative's, if this link was created from the Asset's own tab) needs
    // invalidating too, or it sits stale for the full 5-minute staleTime until something else
    // happens to force a refetch. Without this, a page that shows a rollup over linked artefacts
    // (Asset Spend card, its synced Metric) can silently miss a link created from the other side.
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['linkedArtefacts', artefactId, artefactType] });
      (variables?.target_artefacts || []).forEach((t: any) => {
        queryClient.invalidateQueries({ queryKey: ['linkedArtefacts', t.id, t.type] });
      });
      setSelectedIds([]);
      setSearchQuery('');
      setSearchResults([]);
    },
  });

  const deleteLinkMutation = useMutation({
    mutationFn: (link: LinkedArtefact) =>
      linkedArtefactsApi.deleteLink(artefactId, artefactType, link.artefact_id, link.artefact_type),
    onSuccess: (_data, link) => {
      queryClient.invalidateQueries({ queryKey: ['linkedArtefacts', artefactId, artefactType] });
      queryClient.invalidateQueries({ queryKey: ['linkedArtefacts', link.artefact_id, link.artefact_type] });
    },
  });

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      return;
    }

    // A fast second keystroke can otherwise have its response arrive before a slower first
    // one's, whose now-stale .then still runs and overwrites the fresher results with outdated
    // ones - clearTimeout below only cancels a still-pending timer, not an already in-flight
    // fetch. The controller is created here (not inside the timer callback) so the cleanup
    // closure can always reach it, whether or not the timer has fired yet.
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await linkedArtefactsApi.search(searchQuery, searchType, artefactId, controller.signal);
        setSearchResults(data.results || []);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery, searchType, artefactId]);

  const handleLinkSelected = () => {
    if (selectedIds.length === 0) return;

    const targetArtefacts = selectedIds
      .map((id) => {
        const result = searchResults.find((r) => r.id === id);
        if (!result) return null;
        return {
          id: result.id,
          type: result.type,
          register: result.register,
        };
      })
      .filter(Boolean);

    createLinksMutation.mutate({
      source_id: artefactId,
      source_type: artefactType,
      source_register: artefactRegister,
      target_artefacts: targetArtefacts,
    });
  };

  const handleUnlink = (link: LinkedArtefact) => {
    if (confirm('Are you sure you want to remove this link?')) {
      deleteLinkMutation.mutate(link);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const getTypeName = (type: string): string => {
    const names: Record<string, string> = {
      risk: 'Risk',
      issue: 'Issue',
      incident: 'Incident',
      remediation: 'Remediation',
      evidence: 'Evidence',
      control: 'Control',
      asset: 'Asset',
      document: 'Document',
      person: 'Person',
      threat: 'Threat',
      security_objective: 'Security Objective',
      physical_site: 'Physical Site',
      legal_requirement: 'Legal Requirement',
      interested_party: 'Interested Party',
      ai_use_case: 'AI Use Case',
      meeting_minutes: 'Meeting Minutes',
      subprocessor: 'Controller / Processor',
      initiative: 'Initiative',
      framework: 'Framework',
    };
    return names[type] || type;
  };

  const getTone = (type: string): string => {
    const tones: Record<string, string> = {
      risk: 'danger',
      issue: 'warning',
      incident: 'danger',
      remediation: 'success',
      evidence: 'info',
      control: 'primary',
      asset: 'neutral',
      document: 'info',
      person: 'primary',
      threat: 'danger',
      security_objective: 'primary',
      physical_site: 'neutral',
      legal_requirement: 'warning',
      interested_party: 'info',
      ai_use_case: 'dark',
      meeting_minutes: 'info',
      subprocessor: 'warning',
      initiative: 'primary',
      framework: 'dark',
    };
    return tones[type] || 'neutral';
  };

  const navigateToArtefact = (type: string, id: string, artefactRegister?: string) => {
    const paths: Record<string, string> = {
      risk: `/risks/${id}`,
      issue: `/issues/${id}`,
      incident: `/incidents/${id}`,
      remediation: `/remediations/${id}`,
      evidence: `/evidence/${id}`,
      control: `/controls/${id}`,
      asset: `/assets/${id}`,
      document: `/documents/${id}`,
      person: `/people/${id}`,
      threat: `/threats/${id}`,
      security_objective: `/security-objectives/${id}`,
      physical_site: `/physical-sites/${id}`,
      legal_requirement: `/legal-register/${id}`,
      interested_party: `/interested-parties/${id}`,
      ai_use_case: `/ai-use-cases/${id}`,
      meeting_minutes: `/meeting-minutes/${id}`,
      subprocessor: `/subprocessors/${id}`,
      initiative: `/tracker/initiatives/${id}`,
      framework: `/frameworks/${id}`,
    };
    const path = paths[type];
    if (path) {
      let queryParams = '';
      
      // Controls prefer matrixId when available, others use registerId
      if (type === 'control' && matrixId) {
        queryParams = `?matrixId=${encodeURIComponent(matrixId)}`;
      } else if (type !== 'control') {
        // Use the link's own register first, fall back to the component-level registerId prop
        const effectiveRegisterId = artefactRegister || registerId;
        if (effectiveRegisterId) {
          queryParams = `?registerId=${encodeURIComponent(effectiveRegisterId)}`;
        }
      }
      // Note: Allow navigation even without parameters - let destination page handle it
      
      window.location.href = `${path}${queryParams}`;
    }
  };

  return (
    <div className="col-12 mb-4">
      <div className="np-card">
          <div className="np-card-head">
            <h2><i className="ri-links-line"></i> Linked Artefacts</h2>
          </div>
          <div className="np-card-body">
            {/* Search and Add Section */}
            {!readOnly && (
            <div className="row mb-3">
              <div className="col-md-3 np-field">
                <label>Search Type</label>
                <select
                  className="np-select"
                  value={searchType}
                  onChange={(e) => {
                    setSearchType(e.target.value);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                >
                  <option value="">All Types</option>
                  <option value="risk">Risk</option>
                  <option value="issue">Issue</option>
                  <option value="incident">Incident</option>
                  <option value="remediation">Remediation</option>
                  <option value="evidence">Evidence</option>
                  <option value="control">Control</option>
                  <option value="asset">Asset</option>
                  <option value="document">Document</option>
                  <option value="person">Person</option>
                  <option value="threat">Threat</option>
                  <option value="security_objective">Security Objective</option>
                  <option value="physical_site">Physical Site</option>
                  <option value="legal_requirement">Legal Requirement</option>
                  <option value="interested_party">Interested Party</option>
                  <option value="ai_use_case">AI Use Case</option>
                  <option value="meeting_minutes">Meeting Minutes</option>
                  <option value="subprocessor">Controller / Processor</option>
                  <option value="initiative">Initiative</option>
                  <option value="framework">Framework</option>
                </select>
              </div>
              <div className="col-md-6 np-field">
                <label>
                  Search and Link Artefacts
                  <span className="np-chip np-tone-info ms-2">Multi-Select</span>
                </label>
                <input
                  type="text"
                  className="np-input"
                  placeholder="Type at least 3 characters to search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <small className="np-hint">
                  <i className="ri-information-line"></i> Type to search, then select items below
                </small>

                {/* Search Results */}
                {searchQuery.length >= 3 && (
                  <div className="mt-2 border rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {isSearching ? (
                      <div className="p-3 text-center np-sub">
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        Searching...
                      </div>
                    ) : searchResults.length === 0 ? (
                      <div className="p-3 text-center np-sub"><i className="ri-links-line me-1"></i>No results found</div>
                    ) : (
                      <div>
                        {searchResults.map((result) => (
                          <div
                            key={result.id}
                            className={`np-popover-item ${selectedIds.includes(result.id) ? 'active' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleSelection(result.id)}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input me-2"
                              checked={selectedIds.includes(result.id)}
                              onChange={() => toggleSelection(result.id)}
                            />
                            <div>
                              <span className={`np-chip np-tone-${getTone(result.type)} me-2`}>
                                {getTypeName(result.type)}
                              </span>
                              {/* For assets, people, and documents, show only title. For others, show ID - Title */}
                              {['asset', 'person', 'document'].includes(result.type) ? (
                                <>{typeof result.title === 'object' && result.title && 'value' in result.title ? (result.title as any).value : result.title}</>
                              ) : (
                                <>{result.display_id || result.id} - {typeof result.title === 'object' && result.title && 'value' in result.title ? (result.title as any).value : result.title}</>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="col-md-3 np-field">
                <label>&nbsp;</label>
                <button
                  className="np-btn np-btn-primary w-100"
                  onClick={handleLinkSelected}
                  disabled={selectedIds.length === 0 || createLinksMutation.isPending}
                >
                  <i className="ri-link"></i>
                  Link Selected ({selectedIds.length})
                </button>
              </div>
            </div>
            )}

            {/* Filters */}
            <div className="row mb-3">
              <div className="col-md-4 np-field">
                <label>Filter by Type</label>
                <select className="np-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <option value="">All Types</option>
                  <option value="risk">Risk</option>
                  <option value="issue">Issue</option>
                  <option value="incident">Incident</option>
                  <option value="remediation">Remediation</option>
                  <option value="evidence">Evidence</option>
                  <option value="control">Control</option>
                  <option value="asset">Asset</option>
                  <option value="document">Document</option>
                  <option value="person">Person</option>
                  <option value="threat">Threat</option>
                  <option value="security_objective">Security Objective</option>
                  <option value="physical_site">Physical Site</option>
                  <option value="legal_requirement">Legal Requirement</option>
                  <option value="interested_party">Interested Party</option>
                  <option value="ai_use_case">AI Use Case</option>
                  <option value="meeting_minutes">Meeting Minutes</option>
                  <option value="subprocessor">Controller / Processor</option>
                  <option value="initiative">Initiative</option>
                  <option value="framework">Framework</option>
                </select>
              </div>
            </div>

            {/* Linked Items Table */}
            {isLoading ? (
              <div className="d-flex justify-content-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : filteredLinks.length === 0 ? (
              <div className="text-center py-5">
                <i className="ri-links-line ri-48px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
                <p className="np-sub mb-0">No linked artefacts yet. Use the search above to link related items.</p>
              </div>
            ) : (
              <NimbusTable thead={<tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Accountable Party</th>
                    <th style={{ width: '100px' }}>Actions</th>
                  </tr>}>
                    {filteredLinks.map((link) => (
                      <tr
                        key={link.linkId}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigateToArtefact(link.artefact_type, link.artefact_id, link.artefact_register)}
                      >
                        <td className="np-tabular">{link.artefact_display_id || link.artefact_id}</td>
                        <td>
                          <span className={`np-chip np-tone-${getTone(link.artefact_type)}`}>
                            {getTypeName(link.artefact_type)}
                          </span>
                        </td>
                        <td>
                          <span className="np-row-title">
                            {typeof link.artefact_title === 'object' && link.artefact_title && 'value' in link.artefact_title
                              ? (link.artefact_title as any).value
                              : link.artefact_title}
                          </span>
                        </td>
                        <td>
                          {typeof link.artefact_accountable === 'object' && link.artefact_accountable && 'value' in link.artefact_accountable
                            ? (link.artefact_accountable as any).value
                            : (link.artefact_accountable || '-')}
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="np-icon-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToArtefact(link.artefact_type, link.artefact_id, link.artefact_register);
                              }}
                              title="View"
                            >
                              <i className="ri-eye-line"></i>
                            </button>
                            {!readOnly && (
                              <button
                                className="np-icon-btn np-icon-btn-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnlink(link);
                                }}
                                title="Unlink"
                              >
                                <i className="ri-link-unlink"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
              </NimbusTable>
            )}
          </div>
        </div>
      </div>
  );
};
